import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowCircleDown } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { mfgApi } from './mfgApi';
import { normalizeTehai, tehaiError, TEHAI_PATTERN, TEHAI_HINT } from '@/lib/tehaiNo';

const NONE = '__none__';

/**
 * 出庫（使用・払出）— A2。
 *
 * 記録するのは「誰が・どの仕事で・なぜ・いくつ（基本単位）」。実行者は
 * ログイン中の本人なので画面では聞かない（API が actor を書く）。
 * ロット番号は聞かない — 古いロットから自動で引く（A3: ロット管理 OFF の品目でも同じ）。
 */
export default function IssueStockDialog({
  open, onClose, item, onDone, lockedRef = null, locations = [],
}) {
  const { t } = useT();
  const [meta, setMeta] = useState({ reasons: [], ref_types: [] });
  const [refs, setRefs] = useState({ work_order: [], development: [] });
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    mfgApi.issueReasons().then(setMeta).catch(() => {});
    setF({
      quantity: '', reason_code: 'production', reason_note: '', tehai_no: '',
      ref_type: lockedRef?.ref_type || 'none', ref_id: lockedRef?.ref_id || '',
      location_id: '',
    });
    if (!lockedRef) {
      mfgApi.workOrders().then((d) => setRefs((p) => ({ ...p, work_order: Array.isArray(d) ? d : [] })))
        .catch(() => {});
      mfgApi.developmentsForIssue().then((d) => setRefs((p) => ({ ...p, development: Array.isArray(d) ? d : [] })))
        .catch(() => {});
    }
  }, [open, lockedRef]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const refOptions = f.ref_type === 'work_order'
    ? refs.work_order.map((w) => ({ id: w.id, label: `${w.wo_no || w.manufacturing_no || ''} ${w.product_name || ''}`.trim() }))
    : f.ref_type === 'development'
      ? refs.development.map((d) => ({ id: d.id, label: d.title })) : [];

  const tehaiRules = meta.tehai || { pattern: TEHAI_PATTERN, hint: TEHAI_HINT,
    required_reasons: ['production', 'prototype'] };
  const tehaiRequired = (tehaiRules.required_reasons || []).includes(f.reason_code);

  const submit = async () => {
    const qty = Number(f.quantity);
    if (!qty || qty <= 0) { toast.error(t('mfg.issue.needQty')); return; }
    if (f.ref_type !== 'none' && !f.ref_id) { toast.error(t('mfg.issue.needRef')); return; }
    const tehai = normalizeTehai(f.tehai_no);
    const tehaiBad = tehaiError(f.tehai_no, { required: tehaiRequired,
      pattern: tehaiRules.pattern || TEHAI_PATTERN, hint: tehaiRules.hint || TEHAI_HINT });
    if (tehaiBad) { toast.error(tehaiBad); return; }
    setBusy(true);
    try {
      const res = await mfgApi.issue({
        item_id: item.id, quantity: qty,
        location_id: f.location_id && f.location_id !== NONE ? f.location_id : null,
        reason_code: f.reason_code, reason_note: f.reason_note || null,
        tehai_no: tehai || null,
        ref_type: f.ref_type, ref_id: f.ref_type === 'none' ? null : f.ref_id,
      });
      toast.success(t('mfg.issue.done', { qty, uom: res.uom || '', onHand: res.on_hand }));
      onDone?.(res);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
    finally { setBusy(false); }
  };

  if (!item) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="issue-stock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowCircleDown size={18} /> {t('mfg.issue.title')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-800" data-testid="issue-item-name">
            {item.name}
            <span className="ml-2 font-mono text-xs text-zinc-600">{item.sku}</span>
            <span className="ml-2 text-xs text-zinc-600">
              {t('mfg.issue.onHand')}: {item.on_hand ?? item.quantity ?? '—'}{item.uom || ''}
            </span>
          </p>

          <div>
            <Label className="text-xs font-semibold text-zinc-700">
              {t('mfg.issue.qty')}（{item.uom || ''}）
            </Label>
            <Input type="number" min="0" step="any" value={f.quantity || ''}
              onChange={(e) => set('quantity')(e.target.value)}
              className="mt-1 h-10" data-testid="issue-qty-input" />
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-700">
              手配番号 {tehaiRequired && <span className="text-rose-500">＊</span>}
            </Label>
            <Input value={f.tehai_no || ''} onChange={(e) => set('tehai_no')(e.target.value)}
              placeholder="例 026100105（スキャンまたは入力）" inputMode="numeric"
              className="mt-1 h-10 font-mono" data-testid="issue-tehai-input" />
            <p className="mt-0.5 text-[11px] text-zinc-500" data-testid="issue-tehai-hint">
              {tehaiRules.hint || TEHAI_HINT}
              {tehaiRequired ? '／製造使用・試作使用では必須' : '／この理由では任意'}
            </p>
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-700">{t('mfg.issue.reason')}</Label>
            <Select value={f.reason_code} onValueChange={set('reason_code')}>
              <SelectTrigger className="mt-1 h-10" data-testid="issue-reason-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {meta.reasons.map((r) => (
                  <SelectItem key={r.id} value={r.id} data-testid={`issue-reason-${r.id}`}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!lockedRef && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-semibold text-zinc-700">{t('mfg.issue.refType')}</Label>
                <Select value={f.ref_type}
                  onValueChange={(v) => setF((p) => ({ ...p, ref_type: v, ref_id: '' }))}>
                  <SelectTrigger className="mt-1 h-10" data-testid="issue-ref-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {meta.ref_types.map((r) => (
                      <SelectItem key={r.id} value={r.id} data-testid={`issue-ref-type-${r.id}`}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {f.ref_type !== 'none' && (
                <div>
                  <Label className="text-xs font-semibold text-zinc-700">{t('mfg.issue.ref')}</Label>
                  <Select value={f.ref_id || ''} onValueChange={set('ref_id')}>
                    <SelectTrigger className="mt-1 h-10" data-testid="issue-ref-select">
                      <SelectValue placeholder={t('mfg.issue.refPick')} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72 bg-white">
                      {refOptions.map((o) => (
                        <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          {lockedRef && (
            <p className="text-xs text-zinc-700" data-testid="issue-locked-ref">
              {t('mfg.issue.refType')}: {lockedRef.label}
            </p>
          )}

          {locations.length > 0 && (
            <div>
              <Label className="text-xs font-semibold text-zinc-700">{t('mfg.issue.location')}</Label>
              <Select value={f.location_id || NONE} onValueChange={set('location_id')}>
                <SelectTrigger className="mt-1 h-10" data-testid="issue-location-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72 bg-white">
                  <SelectItem value={NONE}>{t('mfg.issue.anyLocation')}</SelectItem>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.picker_label || l.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs font-semibold text-zinc-700">{t('mfg.issue.note')}</Label>
            <Textarea value={f.reason_note || ''} onChange={(e) => set('reason_note')(e.target.value)}
              rows={2} className="mt-1" data-testid="issue-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="issue-cancel-btn">{t('mfg.cancel')}</Button>
          <Button onClick={submit} disabled={busy} data-testid="issue-submit-btn"
            className="bg-rose-700 hover:bg-rose-800">
            {busy ? t('mfg.saving') : t('mfg.issue.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
